import { MILLISECONDS } from '@chiroup/core/constants/time';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { MeContext } from '../../../../../contexts/me.context';
import { useSandboxClinicalData } from '../../../../../contexts/sandboxClinicalData.context';
import reportService from '../../../../../services/report.service';
import NotesCondition from '../../../../patients/detail/visits/notes/NotesCondition';
import DiscoveryPopup from '../components/DiscoveryPopup';
import WalkthroughCallout from '../components/WalkthroughCallout';
import {
  ConditionListRow,
  matchSandboxConditions,
  unmatchedSandboxDiagnoses,
} from '../fixtures/sandboxConditions';
import { DIAGNOSIS_CAP, DIAGNOSIS_CAP_POPUP, DIAGNOSIS_COPY } from '../sandboxCopy';

interface DiagnosisStepProps {
  selectedConditionIds: number[];
  onToggleCondition: (condition: ConditionListRow, selected: boolean) => void;
  onDone: () => void;
  onBack: () => void;
}

const noop = () => undefined;

/**
 * Step 2 — Select Diagnosis. The ten diagnoses come from the clinic's REAL
 * condition list (a trial clinic's library is the CUP stock protocol set),
 * narrowed by matchSandboxConditions, so the muscles / tests / codes each
 * card carries are the live stock protocol data. Selection is capped at
 * DIAGNOSIS_CAP; the cap attempt shows the "Keeping it simple" pop-up.
 * Favorite hearts stay visible for realism but are inert here.
 */
const DiagnosisStep: React.FC<DiagnosisStepProps> = ({
  selectedConditionIds,
  onToggleCondition,
  onDone,
  onBack,
}) => {
  const [capPopupOpen, setCapPopupOpen] = useState(false);

  const sandbox = useSandboxClinicalData();
  const { me } = useContext(MeContext);

  // Deliberately NOT useConditions: that hook caches every variant under one
  // ['conditions'] key, and the Clinical Protocols page fills it with
  // basicList rows that carry NO muscles/evals/referenceCodes — which
  // silently emptied the diagnosis seeding for any prospect who browsed
  // protocols first (tester note 2026-09-10 #4). A sandbox-scoped key always
  // holds the full rows.
  const { data: conditionsList, isFetching } = useQuery<ConditionListRow[]>(
    ['sandbox-conditions'],
    () =>
      sandbox
        ? sandbox.conditions
        : reportService.listConditions(me?.selectedClinic?.ID || -1, false, false),
    { staleTime: MILLISECONDS.inHours(1), refetchOnWindowFocus: false }
  );

  const sandboxConditions = useMemo(
    () => matchSandboxConditions((conditionsList as unknown as ConditionListRow[]) || []),
    [conditionsList]
  );

  const selectedSet = useMemo(() => new Set(selectedConditionIds), [selectedConditionIds]);

  // Dev diagnostic: a spec diagnosis with no library match renders no card,
  // which QA reads as "missing". Name the gap and where to fix it (library
  // names only — no patient data).
  useEffect(() => {
    if (!import.meta.env.DEV || isFetching || !conditionsList?.length) return;

    const missing = unmatchedSandboxDiagnoses(
      (conditionsList as unknown as ConditionListRow[]) || []
    );
    if (missing.length) {
      console.warn(
        `[soap-sandbox] No condition-library match for: ${missing.join(', ')}. ` +
          'These diagnosis cards are omitted. Check the clinic library for renamed, hidden, or deleted versions of these conditions.'
      );
    }
  }, [conditionsList, isFetching]);

  // Dev diagnostic: muscles, orthopedic tests and ICD codes ALL come off these
  // library rows, so an empty protocol payload silently produces an empty
  // Myofascial module, no orthopedic suggestions, and no diagnostic codes in
  // the note at once. Print what each matched row actually carries so a data
  // gap is distinguishable from a wiring bug (library names only, no PHI).
  useEffect(() => {
    if (!import.meta.env.DEV || isFetching || !sandboxConditions.length) return;

    // Every library row whose name looks lumbar, matched or not: clinic
    // libraries can hold several rows per diagnosis with different protocol
    // data, and we need to see whether a populated duplicate exists.
    const rows = (conditionsList as unknown as ConditionListRow[]) || [];
    console.table(
      rows
        .filter(row =>
          /lumbar|sacroiliac|crossed|extension/i.test(row.title || row.name || row.commonName || '')
        )
        .map(row => ({
          name: row.title || row.name || row.commonName || '',
          ID: row.ID,
          muscles: row.muscles?.length ?? 0,
          pos: row.typicallyPositiveEvals?.length ?? 0,
          codes: row.referenceCodes?.length ?? 0,
          hide: (row as unknown as { hide?: number }).hide,
        }))
    );

    console.table(
      sandboxConditions.map(condition => ({
        name: condition.title || condition.name || condition.commonName || '',
        ID: condition.ID,
        clinicVersion: condition.clinicVersion ?? null,
        userVersion: condition.userVersion ?? null,
        hasMusclesKey: 'muscles' in condition,
        regions: condition.regions?.length ?? 0,
        muscles: condition.muscles?.length ?? 0,
        typicallyPositive: condition.typicallyPositiveEvals?.length ?? 0,
        typicallyNegative: condition.typicallyNegativeEvals?.length ?? 0,
        referenceCodes: condition.referenceCodes?.length ?? 0,
      }))
    );
  }, [sandboxConditions, isFetching]);

  const handleSelect = (condition: ConditionListRow) => {
    if (selectedSet.size >= DIAGNOSIS_CAP) {
      setCapPopupOpen(true);

      return;
    }
    onToggleCondition(condition, true);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
      <div className="flex-1 rounded-lg border border-gray-200 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Select the diagnosis that best fits this patient.
        </h2>

        {isFetching && !sandboxConditions.length ? (
          <div className="mt-6 py-16 text-center text-sm text-gray-400">Loading diagnoses…</div>
        ) : (
          // NotesCondition dims unselected rows to 40% in the picker; the
          // spec mock shows full-opacity cards, so override here.
          <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 [&_li]:opacity-100">
            {sandboxConditions.map(condition => (
              <NotesCondition
                key={condition.ID}
                parentId={condition.ID}
                conditionId={condition.ID}
                name={condition.title || condition.name || condition.commonName || ''}
                image={condition.image || condition.img || null}
                regions={condition.regions || []}
                muscles={condition.muscles || []}
                typicallyPositiveEvals={condition.typicallyPositiveEvals || []}
                typicallyNegativeEvals={condition.typicallyNegativeEvals || []}
                referenceCodes={condition.referenceCodes || []}
                conditionIdSet={selectedSet}
                onChange={() => handleSelect(condition)}
                handleDeleteCondition={() => onToggleCondition(condition, false)}
                complaintIndex={0}
                selectedConditions={undefined}
                // Spec: hearts visible for context but disabled in the sandbox.
                toggleFavorite={noop}
                favorite={Number(condition.favorite) || 0}
              />
            ))}
          </ul>
        )}

        <div className="mt-5 flex items-start gap-3 rounded-xl border-l-4 border border-amber-200 border-l-amber-400 bg-gradient-to-r from-amber-50 to-yellow-50/50 px-4 py-3.5 shadow-sm dark:border-amber-800 dark:border-l-amber-500 dark:from-amber-900/25 dark:to-amber-900/10">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-amber-600 shadow-sm ring-1 ring-inset ring-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-700">
            <span className="font-serif text-sm font-bold italic leading-none">i</span>
          </span>
          <div>
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {DIAGNOSIS_COPY.narrowedNote.split('\n')[0]}
            </p>
            <p className="mt-0.5 text-sm text-amber-700/90 dark:text-amber-300/80">
              {DIAGNOSIS_COPY.narrowedNote.split('\n')[1]}
            </p>
          </div>
        </div>
      </div>

      <div className="lg:w-80 shrink-0">
        <WalkthroughCallout
          stepLabel={DIAGNOSIS_COPY.stepLabel}
          title={DIAGNOSIS_COPY.title}
          body={DIAGNOSIS_COPY.body}
          cta={DIAGNOSIS_COPY.cta}
          onCta={onDone}
          onBack={onBack}
          ctaDisabled={selectedSet.size === 0}
          ctaDisabledHint={DIAGNOSIS_COPY.ctaDisabledHint}
          className="lg:sticky lg:top-4"
        />
      </div>

      {capPopupOpen && (
        <DiscoveryPopup
          title={DIAGNOSIS_CAP_POPUP.title}
          body={DIAGNOSIS_CAP_POPUP.body}
          onDismiss={() => setCapPopupOpen(false)}
          dismissLabel={DIAGNOSIS_CAP_POPUP.cta}
        />
      )}
    </div>
  );
};

export default DiagnosisStep;
